import React, { useState } from "react";
import classNames from "classnames";
import Cleave from "cleave.js/react";

import setHelperText from "../helpers/setHelperText";
import { LoadingSkeleton } from "../../LoadingSkeleton";
import ReadOnlyContainer from "../../ReadOnlyContainer";
import { WrappedFieldInputProps, WrappedFieldMetaProps } from "redux-form";

export interface SsnInputFieldProps {
  id: string;
  label: string;
  type: string;
  input: Partial<WrappedFieldInputProps>;
  helperText?: string;
  disabled?: boolean;
  showRequiredStar?: boolean;
  meta?: Partial<WrappedFieldMetaProps>;
  readOnly?: boolean;
}

export const SsnInputField: React.FC<SsnInputFieldProps> = (props: SsnInputFieldProps): JSX.Element => {
  const { id, label, disabled, type, helperText, meta, input, showRequiredStar, readOnly, ...rest } = props;

  const { onFocus, onBlur, ...inputProps } = input;

  const { touched, error } = meta;
  const [isFocused, setIsFocused] = useState(false);

  const formElemRootClasses = classNames(
    "caprx-f-elem-root",
    { "caprx-f-elem-animate": isFocused || readOnly || input.value !== "" },
    { "caprx-f-elem-error": touched && error },
    { "caprx-f-elem-disabled": disabled },
  );

  const handleOnFocus = (ev: React.FocusEvent): void => {
    setIsFocused(true);
    if (onFocus) onFocus(ev);
  };

  const handleOnBlur = (): void => {
    if (input.value === "") {
      setIsFocused(false);
    }
    if (onBlur) onBlur(input.value);
  };

  const options = { delimiters: ["-", "-"], numericOnly: true, uppercase: true, blocks: [3, 2, 4] };
  const placeholder = "Social Security Number";

  let body = (
    <Cleave
      {...rest}
      {...inputProps}
      id={id}
      type={type}
      disabled={disabled}
      className="caprx-f-elem-input"
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      placeholder={placeholder}
      options={options}
    />
  );

  if (readOnly) {
    body = <ReadOnlyContainer {...rest} id={id} body={input.value ? input.value : "N/A"} />;
  }

  return (
    <LoadingSkeleton>
      <div className={formElemRootClasses}>
        {body}
        <label htmlFor={id} className="caprx-f-elem-label">
          {label}
          {showRequiredStar && <span className="required-star">*</span>}
        </label>
        <div className="caprx-f-elem-helper-text">{setHelperText(helperText, touched, error)}</div>
      </div>
    </LoadingSkeleton>
  );
};

export default SsnInputField;
